import { isFunction } from "@/scripts/helpers/is";
import { ENDPOINTS } from "@/scripts/helpers/constants";

const {
  GET_ACTIVE_SUBSCRIPTION_V1,
  GET_ALL_SUBSCRIPTIONS_V1,
  CANCEL_SUBSCRIPTION,
  FREE_UPGRADE_SUBSCRIPTION,
  DEFAULT_METHOD,
  GET_GATEWAY_TOKEN,
  METHODS,
  USER_INFO_V1,
  USER_INFO_PASSWORD,
  USER_INFO_EMAIL,
  VERIFY_EMAIL,
  GET_USER_INFO_WITH_FORMS,
  GET_USER_INFO_WITH_FORMS_AND_PROJECTS,
  UPGRADE_TO_PREMIUM,
  GET_UPGRADE_PLANS_INFO,
  ADD_USER_TO_EXPERIMENT,
  PLAN_INFO_PREMIUM,
  GET_DEFINITIONS,
  GET_LEGAL_LIFE_ARTICLES,
  GET_HELPFUL_TOPICS,
  GET_LEGAL_QUESTIONS_CATEGORY,
  GET_LEGAL_QUESTION,
  CREATE_DISCOUNT,
  SUBSCRIPTION_CANCEL_REASON_LIST,
  SUBSCRIPTION_CANCEL_ANSWER,
  GET_SUBSCRIPTION_BASED_FORMS,
  GET_PURCHASED_FORMS,
  GET_PROJECT_FORMS,
  USER_LOCATION
} = ENDPOINTS;

export const simpleGetResponse = (url, withCredentials = false) => (
  successCallback,
  errorCallback
) => {
  return fetch(url, {
    ...(withCredentials && { credentials: "same-origin" }),
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  }).then(response => {
    if (response.ok) {
      if (isFunction(successCallback)) successCallback(response);
      return response.json();
    } else {
      if (isFunction(errorCallback)) errorCallback();
      return response.json();
    }
  });
};

const updateUserDetails = (url, data) => (successCallback, errorCallback) => {
  return fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    method: "PATCH",
    body: JSON.stringify(data)
  }).then(response => {
    if (response.ok) {
      if (isFunction(successCallback)) successCallback();
      return response.json();
    } else {
      if (isFunction(errorCallback)) errorCallback(response);
    }
  });
};

const getUserInfo = simpleGetResponse(USER_INFO_V1);

const getUserFormInfo = simpleGetResponse(GET_USER_INFO_WITH_FORMS, true);

const getUserFormAndProjectsInfo = simpleGetResponse(
  GET_USER_INFO_WITH_FORMS_AND_PROJECTS,
  true
);

const getSubscription = simpleGetResponse(GET_ACTIVE_SUBSCRIPTION_V1);

const getAllSubscriptions = simpleGetResponse(GET_ALL_SUBSCRIPTIONS_V1);

const getSubscriptionWithPlan = simpleGetResponse(GET_ACTIVE_SUBSCRIPTION_V1);

const getDefaultMethod = simpleGetResponse(DEFAULT_METHOD);

const updateUserName = data => updateUserDetails(USER_INFO_V1, data);

const updateUserEmail = data => updateUserDetails(USER_INFO_EMAIL, data);

const updateUserPassword = data => updateUserDetails(USER_INFO_PASSWORD, data);

const verifyUserEmail = (source = null) => (successCallback, errorCallback) => {
  const url = source ? `${VERIFY_EMAIL}?source=${source}` : VERIFY_EMAIL;

  return fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    method: "POST"
  }).then(response => {
    if (response.ok) {
      if (isFunction(successCallback)) successCallback();
      return response.json();
    } else {
      if (isFunction(errorCallback)) errorCallback(response);
    }
  });
};

const getGatewayToken = simpleGetResponse(GET_GATEWAY_TOKEN, true);

const getPremiumPlanInfo = simpleGetResponse(PLAN_INFO_PREMIUM);

const freeUpgradeSubscription = () =>
  fetch(FREE_UPGRADE_SUBSCRIPTION, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  });

const cancelSubscription = () =>
  fetch(CANCEL_SUBSCRIPTION, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "Content-Length": "0"
    }
  });

const enableSubscription = id =>
  fetch(`/api/v1/subscriptions/${id}/enable`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  });

const updatePaymentMethod = (nonce, methodType) => {
  const data = { nonce, method_type: methodType };

  return fetch(METHODS, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data)
  });
};

const upgradeToPremium = planId => {
  const data = { plan_id: planId };
  return fetch(UPGRADE_TO_PREMIUM, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    method: "PATCH",
    body: JSON.stringify(data)
  });
};

const getPlanInfoById = id => {
  return fetch(`/api/v1/plans/${id}`, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    method: "GET",
    credentials: "include"
  }).then(response => {
    return response.json();
  });
};

const getUpgradePlansInfo = simpleGetResponse(GET_UPGRADE_PLANS_INFO);

const addUserToExperiment = uid => {
  const data = { uid: uid };

  return fetch(ADD_USER_TO_EXPERIMENT, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    credentials: "same-origin",
    method: "POST",
    body: JSON.stringify(data)
  }).then(response => {
    return response.json();
  });
};

const getPopularDefinitions = simpleGetResponse(
  `${GET_DEFINITIONS}?filter[is_popular]=1&per_page=60`
);

const getDefinitionById = id => simpleGetResponse(`${GET_DEFINITIONS}/${id}`)();

const getDefinitionsBySearch = query =>
  simpleGetResponse(
    `${GET_DEFINITIONS}?filter[word]=${query}&per_page=10000`
  )();

const getDefinitionsByFirstLetter = letter =>
  simpleGetResponse(
    `${GET_DEFINITIONS}?filter[letter]=${letter}&per_page=10000`
  )();

const getLegalLifeArticles = simpleGetResponse(GET_LEGAL_LIFE_ARTICLES);

const getLegalLifeArticleById = id =>
  simpleGetResponse(`${GET_LEGAL_LIFE_ARTICLES}/${id}`)();

const getHelpfulTopics = simpleGetResponse(`${GET_HELPFUL_TOPICS}?per_page=90`);

const getHelpfulTopicsById = id =>
  simpleGetResponse(`${GET_HELPFUL_TOPICS}/${id}`)();

const getLegalQuestions = simpleGetResponse(
  `${GET_LEGAL_QUESTIONS_CATEGORY}?per_page=500`
);

const getLegalQuestionsById = id =>
  simpleGetResponse(
    `${GET_LEGAL_QUESTIONS_CATEGORY}/${id}?include=questions_and_answers`
  )();

const getLegalQuestionsPostById = id =>
  simpleGetResponse(`${GET_LEGAL_QUESTION}/${id}?include=category`)();

const createDiscount = () => {
  return fetch(CREATE_DISCOUNT, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    }
  });
};

const getSubscriptionCancelReasonList = simpleGetResponse(
  SUBSCRIPTION_CANCEL_REASON_LIST
);

const sendSubscriptionCancelAnswer = (id, answer) => {
  const data = { reason_id: id, custom_answer: answer };

  return fetch(SUBSCRIPTION_CANCEL_ANSWER, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json"
    },
    body: JSON.stringify(data)
  });
};

const userSubscriptionBasedForms = simpleGetResponse(
  GET_SUBSCRIPTION_BASED_FORMS,
  true
);

const userPurchasedForms = simpleGetResponse(GET_PURCHASED_FORMS, true);

const getProjectsForms = simpleGetResponse(GET_PROJECT_FORMS, true);

const getUserLocation = () => {
  return fetch(USER_LOCATION, {
    Accept: "application/json",
    "Content-Type": "application/json"
  }).then(response => {
    return response.json();
  });
};

export const api = {
  getUserInfo,
  getUserFormAndProjectsInfo,
  getSubscription,
  getAllSubscriptions,
  getDefaultMethod,
  getGatewayToken,
  updatePaymentMethod,
  updateUserName,
  updateUserEmail,
  updateUserPassword,
  verifyUserEmail,
  getUserFormInfo,
  cancelSubscription,
  freeUpgradeSubscription,
  upgradeToPremium,
  getSubscriptionWithPlan,
  getUpgradePlansInfo,
  getPlanInfoById,
  addUserToExperiment,
  getPopularDefinitions,
  getDefinitionById,
  getDefinitionsBySearch,
  getDefinitionsByFirstLetter,
  getLegalLifeArticles,
  getPremiumPlanInfo,
  getLegalLifeArticleById,
  getHelpfulTopics,
  getHelpfulTopicsById,
  getLegalQuestions,
  getLegalQuestionsById,
  getLegalQuestionsPostById,
  createDiscount,
  getSubscriptionCancelReasonList,
  sendSubscriptionCancelAnswer,
  userSubscriptionBasedForms,
  userPurchasedForms,
  getProjectsForms,
  getUserLocation,
  enableSubscription
};
